'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { WishlistApi } from '@/lib/Apis/apis/wishlist-api';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { useAuth } from './AuthProvider';
import { useMemo } from 'react';
import { ProductsApi, Wishlist, Product } from '@/lib/Apis';
import { useNotification } from './NotificationProvider';
import {
  readGuestWishlist,
  writeGuestWishlist,
  clearGuestWishlist,
  addToGuestWishlist,
  removeFromGuestWishlist,
} from '@/lib/utils/guestWishlist';


interface WishlistContextType extends Wishlist {
  addToWishlist: (product: Product) => Promise<boolean>;
  removeFromWishlist: (productId: string) => Promise<void>;
  isInWishlist: (productId: string) => boolean;
  getWishlistCount: () => number;
  refreshWishlist: () => Promise<void>;
  clearWishlist: () => Promise<void>;
  mergeGuestWishlist: () => Promise<void>;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Wishlist>({
    _id: '',
    createdAt: new Date(),
    updatedAt: new Date(),
    userId: '',
    products: [],
  } as unknown as Wishlist);

  const { isAuthenticated } = useAuth() || {};
  const notification = useNotification();
  const wishlistApi = useMemo(() => new WishlistApi(AXIOS_CONFIG), []);

  const fetchWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      const guestProducts = readGuestWishlist();
      setState(prev => ({
        ...prev,
        isLoading: false,
        products: guestProducts,
      }));
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await wishlistApi.getWishlist();
      const responseData = response?.data;

      setState({
        ...responseData,
        isLoading: false,
        error: null,
      } as unknown as Wishlist);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load wishlist',
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);


  const isInWishlist = useCallback((productId: string): boolean => {
    if (!state.products) return false;
    return state.products.some((id: any) => {
      const idToCompare = typeof id === 'string' ? id : id?._id;
      return idToCompare === productId;
    });
  }, [state.products]);

  const getWishlistCount = useCallback((): number => {
    return Math.max(0, state.products?.length || 0);
  }, [state.itemCount, state.products]);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const addToWishlist = useCallback(async (product: Product): Promise<boolean> => {
    const productId = product?._id || '';

    if (!isAuthenticated) {
      if (isInWishlist(productId)) return false;

      const newProducts = addToGuestWishlist(productId);
      setState(prev => ({
        ...prev,
        products: newProducts,
        itemCount: newProducts.length,
      }));
      notification.success(
        'Added to wishlist',
        `${product?.name || 'Item'} was added to your wishlist.`
      );
      return true;
    }

    try {
      if (isInWishlist(productId)) {
        return false;
      }

      // Optimistic update
      setState(prev => {
        const currentProducts = prev.products || [];
        const newProducts = [...currentProducts, productId];
        return {
          ...prev,
          products: newProducts,
          itemCount: (prev.itemCount || 0) + 1,
          isLoading: false,
          error: null,
        };
      });

      notification.success(
        'Added to wishlist',
        `${product?.name || 'Item'} was added to your wishlist.`
      );

      const response = await wishlistApi.addToWishlist(productId);
      const responseData = response?.data;

      if (responseData) {
        setState({
          ...responseData,
          isLoading: false,
          error: null,
        } as unknown as Wishlist);
      }

      return true;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      await fetchWishlist();
      notification.error(
        'Could not add to wishlist',
        'Something went wrong while adding this item. Please try again.'
      );
      throw error;
    }
  }, [isAuthenticated, isInWishlist, wishlistApi, fetchWishlist, notification]);

  const removeFromWishlist = useCallback(async (productId: string) => {
    if (!isAuthenticated) {
      const newProducts = removeFromGuestWishlist(productId);
      setState(prev => ({
        ...prev,
        products: newProducts,
        itemCount: newProducts.length,
      }));
      notification.success(
        'Removed from wishlist',
        'The item has been removed from your wishlist.'
      );
      return;
    }

    try {
      // Optimistic update
      setState(prev => {
        const newProducts = prev.products.filter((id: any) => {
          const idToCompare = typeof id === 'string' ? id : id?._id;
          return idToCompare !== productId;
        });
        return {
          ...prev,
          products: newProducts,
          itemCount: Math.max(0, (prev.itemCount || 0) - 1),
        };
      });

      const response = await wishlistApi.removeFromWishlist(productId);
      const responseData = response?.data;

      if (responseData) {
        setState({
          ...responseData,
          isLoading: false,
          error: null,
        } as unknown as Wishlist);
      }

      notification.success(
        'Removed from wishlist',
        'The item has been removed from your wishlist.'
      );
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      await fetchWishlist();
      notification.error(
        'Could not remove item',
        'Something went wrong while removing this item. Please try again.'
      );
      throw error;
    }
  }, [isAuthenticated, wishlistApi, fetchWishlist, notification]);

  const clearWishlist = useCallback(async () => {
    if (!isAuthenticated) {
      clearGuestWishlist();
      setState(prev => ({
        ...prev,
        products: [],
        itemCount: 0,
      }));
      notification.success(
        'Wishlist cleared',
        'All items have been removed from your wishlist.'
      );
      return;
    }

    try {
      await wishlistApi.clearWishlist();
      setState(prev => ({
        ...prev,
        products: [],
        itemCount: 0,
      }));
      notification.success(
        'Wishlist cleared',
        'All items have been removed from your wishlist.'
      );
    } catch (error) {
      console.error('Error clearing wishlist:', error);
      notification.error(
        'Could not clear wishlist',
        'We could not clear your wishlist. Please try again.'
      );
      throw error;
    }
  }, [isAuthenticated, wishlistApi, notification]);

  const refreshWishlist = useCallback(async () => {
    await fetchWishlist();
  }, [fetchWishlist]);

  const mergeGuestWishlist = useCallback(async () => {
    const guestProductIds = readGuestWishlist();
    if (guestProductIds.length === 0) return;

    try {
      // Fetch current server wishlist
      let serverProductIds: string[] = [];
      try {
        const res = await wishlistApi.getWishlist();
        serverProductIds = (res.data?.products || []).map((id: any) =>
          typeof id === 'string' ? id : id?._id
        );
      } catch {
        // Server wishlist may be empty
      }

      // Add guest items not already on server
      const toAdd = guestProductIds.filter((id) => !serverProductIds.includes(id));
      await Promise.all(toAdd.map((id) => wishlistApi.addToWishlist(id).catch(() => null)));

      clearGuestWishlist();
      await fetchWishlist();
    } catch (error) {
      console.error('Failed to merge guest wishlist:', error);
      clearGuestWishlist();
    }
  }, [wishlistApi, fetchWishlist]);

  return (
    <WishlistContext.Provider
      value={{
        ...state,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        getWishlistCount,
        refreshWishlist,
        clearWishlist,
        mergeGuestWishlist,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = (): WishlistContextType => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
